import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { logError } from '../utils/log';

export const SettingsPage = () => {
    const [inviteCodes, setInviteCodes] = useState([]);
    const [newCode, setNewCode] = useState('');
    const [isForAdmin, setIsForAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [schedules, setSchedules] = useState([]);
    const [players, setPlayers] = useState([]);
    const [selectedSchedule, setSelectedSchedule] = useState('');
    const [selectedWeekIndex, setSelectedWeekIndex] = useState('');
    const [selectedArchiveSchedule, setSelectedArchiveSchedule] = useState('');
    const [selectedImpersonationId, setSelectedImpersonationId] = useState('');

    const [message, setMessage] = useState('');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const codesPromise = getDocs(collection(db, "invite_codes"));
            const schedulesPromise = getDocs(collection(db, "schedules"));
            const usersPromise = getDocs(collection(db, "users"));

            const [codesSnapshot, schedulesSnapshot, usersSnapshot] = await Promise.all([codesPromise, schedulesPromise, usersPromise]);

            setInviteCodes(codesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            
            const schedulesList = schedulesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSchedules(schedulesList);
            if (schedulesList.length > 0) {
                setSelectedSchedule(current => schedulesList.some(s => s.id === current) ? current : schedulesList[0].id);
                setSelectedArchiveSchedule(current => schedulesList.some(s => s.id === current) ? current : schedulesList[0].id);
            }

            setPlayers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        } catch (error) {
            logError("Error fetching settings data: ", error);
            setMessage("Failed to load page data.");
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleCreateCode = async (e) => {
        e.preventDefault();
        if (newCode.trim() === '') {
            setMessage('Invite code cannot be empty.');
            return;
        }
        try {
            const codesCollectionRef = collection(db, "invite_codes");
            await addDoc(codesCollectionRef, {
                code: newCode.trim(),
                isForAdmin: isForAdmin // Save the admin flag
            });
            setMessage(`Invite code "${newCode.trim()}" created successfully.`);
            setNewCode('');
            setIsForAdmin(false); // Reset checkbox after creation
            fetchData(); // Refresh the list
        } catch (error) {
            setMessage("Error creating invite code.");
            logError("Error creating code: ", error);
        }
    };

    const handleDeleteCode = async (codeId) => {
        if (window.confirm("Are you sure you want to delete this invite code?")) {
            try {
                const codeDocRef = doc(db, "invite_codes", codeId);
                await deleteDoc(codeDocRef);
                setMessage("Invite code deleted successfully.");
                fetchData(); // Refresh the list
            } catch (error) {
                setMessage("Error deleting invite code.");
                logError("Error deleting code: ", error);
            }
        }
    };

    const handleViewAsUser = () => {
        if (!selectedImpersonationId) {
            setMessage("Please select a user to view as.");
            return;
        }
        // The logic in App.js would need to check if the *actual* user is an admin.
        // This action is only available on an admin page, so we assume the current user is an admin.
        sessionStorage.setItem('impersonatingUserId', selectedImpersonationId);
        window.location.reload();
    };

    const handleStopViewing = () => {
        sessionStorage.removeItem('impersonatingUserId');
        window.location.reload();
    };


    const handleDeleteSchedule = async () => {
        if (!selectedSchedule) {
            alert("Please select a season (schedule) to delete.");
            return;
        }

        const scheduleToDelete = schedules.find(s => s.id === selectedSchedule);
        if (!scheduleToDelete) {
            alert("Selected schedule not found.");
            return;
        }

        if (window.confirm(`Are you sure you want to permanently delete the "${scheduleToDelete.name}" season? This will remove all associated data. This action cannot be undone.`)) {
            try {
                await deleteDoc(doc(db, "schedules", selectedSchedule));
                alert(`Season "${scheduleToDelete.name}" has been deleted.`);
                fetchData(); // Refresh the list
            } catch (error) {
                alert("Error deleting season. Check console for details.");
                logError("Error deleting schedule: ", error);
            }
        }
    };

    const handleCleanWeek = async () => {
        if (!selectedSchedule || selectedWeekIndex === '') {
            alert("Please select a season and a week to clean.");
            return;
        }

        const scheduleToUpdate = schedules.find(s => s.id === selectedSchedule);
        if (!scheduleToUpdate) {
            alert("Selected schedule not found.");
            return;
        }

        const weekToClean = scheduleToUpdate.weeks[selectedWeekIndex];
        const weekName = weekToClean.week === 'Event' ? weekToClean.games[0].eventName : `Week ${weekToClean.week}`;

        if (window.confirm(`Are you sure you want to erase all match results for "${weekName}" in the "${scheduleToUpdate.name}" season? This action cannot be undone.`)) {
            try {
                const updatedWeeks = JSON.parse(JSON.stringify(scheduleToUpdate.weeks));
                const targetWeek = updatedWeeks[selectedWeekIndex];

                targetWeek.games.forEach(game => {
                    delete game.results;
                    delete game.result;
                });

                const scheduleDocRef = doc(db, "schedules", selectedSchedule);
                await updateDoc(scheduleDocRef, { weeks: updatedWeeks });

                alert(`All results for "${weekName}" have been cleared successfully.`);
                fetchData(); // Refresh data to reflect changes
            } catch (error) {
                alert("Error cleaning week results. Check console for details.");
                logError("Error cleaning week: ", error);
            }
        }
    };

    // --- Archive Season ---
    const handleArchiveSeason = async () => {
        if (!selectedArchiveSchedule) {
            alert("Please select a season to archive.");
            return;
        }
        const scheduleToArchive = schedules.find(s => s.id === selectedArchiveSchedule);
        if (!scheduleToArchive) {
            alert("Selected schedule not found.");
            return;
        }
        if (scheduleToArchive.archived) {
            alert(`Season "${scheduleToArchive.name}" is already archived.`);
            return;
        }
        if (window.confirm(`Archive the season "${scheduleToArchive.name}"? It will be treated as historical and can be hidden from schedule pages.`)) {
            try {
                await updateDoc(doc(db, 'schedules', selectedArchiveSchedule), { archived: true });
                setMessage(`Season "${scheduleToArchive.name}" archived.`);
                fetchData();
            } catch (error) {
                logError('Error archiving season: ', error);
                setMessage('Error archiving season.');
            }
        }
    };

    // Temporary: Initialize archive field on all schedules
    const handleInitializeArchiveField = async () => {
        try {
            setMessage('Initializing archive field on schedules...');
            const snapshot = await getDocs(collection(db, 'schedules'));
            let updated = 0;
            for (const d of snapshot.docs) {
                const data = d.data();
                if (typeof data.archived === 'undefined') {
                    await updateDoc(doc(db, 'schedules', d.id), { archived: false });
                    updated++;
                }
            }
            setMessage(`Initialization complete. Schedules updated: ${updated}.`);
            fetchData();
        } catch (error) {
            logError('Error initializing archive field: ', error);
            setMessage('Error initializing archive field.');
        }
    };

    const impersonatedUserId = sessionStorage.getItem('impersonatingUserId');
    const impersonatedUser = impersonatedUserId ? players.find(p => p.id === impersonatedUserId) : null;

    return (
        <div className="space-y-4">
            <h2 className="page-title heading-primary-1 text-4xl font-extrabold text-center mb-6">Admin Settings</h2>
            
            {/* View As User Section */}
            <div className="card p-6 rounded-lg shadow-md">
                <h3 className="heading-primary-1 text-2xl font-semibold mb-4">Impersonate User</h3>
                {impersonatedUser ? (
                    <div className="bg-info-soft border-l-4 border-info p-4 rounded-md mb-4">
                        <p className="font-bold text-info">Currently viewing as: {impersonatedUser.nickname || impersonatedUser.firstName}</p>
                        <p className="text-sm text-muted">To return to your admin view, click "Stop Viewing".</p>
                    </div>
                ) : (
                    <p className="text-sm text-muted mb-4">
                        Select a user to view the application from their perspective. This is useful for troubleshooting and verifying user-specific views.
                    </p>
                )}
                <div className="flex flex-wrap items-center gap-4">
                    <select value={selectedImpersonationId} onChange={(e) => setSelectedImpersonationId(e.target.value)} className="flex-grow shadow border-low border rounded w-full md:w-auto py-2 px-3 text-muted leading-tight focus:outline-none focus:shadow-outline surface-low">
                        <option value="">Select a User to View As</option>
                        {players.sort((a, b) => (a.firstName || '').localeCompare(b.firstName || '')).map(player => (
                            <option key={player.id} value={player.id}>{player.nickname || `${player.firstName} ${player.lastName}`} ({player.email})</option>
                        ))}
                    </select>
                    <button onClick={handleViewAsUser} className="btn btn-info font-bold py-2 px-4 rounded-lg" disabled={!selectedImpersonationId}>View as User</button>
                    {impersonatedUserId && (<button onClick={handleStopViewing} className="btn btn-secondary font-bold py-2 px-4 rounded-lg">Stop Viewing</button>)}
                </div>
            </div>

            {/* Invite Code Management Section */}
            <div className="card p-6 rounded-lg shadow-md">
                <h3 className="heading-primary-1 text-2xl font-semibold mb-4">Manage Invite Codes</h3>
                {message && <p className="text-center mb-4 text-success">{message}</p>}
                <form onSubmit={handleCreateCode} className="mb-6">
                    <div className="flex flex-wrap items-center gap-4">
                        <input 
                            type="text" 
                            value={newCode}
                            onChange={(e) => setNewCode(e.target.value)}
                            placeholder="Enter new invite code"
                            className="flex-grow shadow appearance-none border-low border rounded w-full md:w-auto py-2 px-3 text-muted leading-tight focus:outline-none focus:shadow-outline surface-low"
                        />
                        <button type="submit" className="btn btn-primary font-bold py-2 px-4 rounded-lg">Add Code</button>
                    </div>
                    <div className="mt-2">
                        <label className="flex items-center text-sm">
                            <input type="checkbox" checked={isForAdmin} onChange={(e) => setIsForAdmin(e.target.checked)} className="mr-2" />
                            For Admin Registration
                        </label>
                    </div>
                </form>

                <h4 className="heading-primary-1 text-xl font-semibold mb-2">Current Invite Codes:</h4>
                {loading ? <p>Loading codes...</p> : (
                    <ul className="list-disc list-inside space-y-2">
                        {inviteCodes.map(code => (
                            <li key={code.id} className="flex justify-between items-center">
                                <span>
                                    <span className="font-mono">{code.code}</span>
                                    {code.isForAdmin && <span className="ml-2 text-xs font-bold badge subheading-primary-1 px-2 py-1 rounded-full">ADMIN</span>}
                                </span>
                                <button onClick={() => handleDeleteCode(code.id)} className="btn btn-danger text-xs font-bold py-1 px-2 rounded-md">Delete</button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Weekly Maintenance Section */}
            <div className="card p-6 rounded-lg shadow-md">
                <h3 className="heading-warning text-2xl font-semibold mb-4">Weekly Maintenance: Clear Week Results</h3>
                <p className="text-sm text-muted mb-4">
                    This tool will erase all entered match results for a specific week within a season, resetting it to an unplayed state. This is useful for correcting large-scale entry errors.
                </p>
                <div className="flex flex-wrap items-center gap-4">
                    <select
                        value={selectedSchedule}
                        onChange={(e) => {
                            setSelectedSchedule(e.target.value);
                            setSelectedWeekIndex(''); // Reset week selection when schedule changes
                        }}
                        className="flex-grow shadow border-low border rounded w-full md:w-auto py-2 px-3 text-muted leading-tight focus:outline-none focus:shadow-outline surface-low"
                        disabled={schedules.length === 0}
                    >
                        <option value="">Select a Season</option>
                        {schedules.map(schedule => (
                            <option key={schedule.id} value={schedule.id}>{schedule.name}</option>
                        ))}
                    </select>
                    <select
                        value={selectedWeekIndex}
                        onChange={(e) => setSelectedWeekIndex(e.target.value)}
                        className="flex-grow shadow border-low border rounded w-full md:w-auto py-2 px-3 text-muted leading-tight focus:outline-none focus:shadow-outline surface-low"
                        disabled={!selectedSchedule}
                    >
                        <option value="">Select a Week</option>
                        {schedules.find(s => s.id === selectedSchedule)?.weeks.map((week, index) => (
                            <option key={index} value={index}>{week.week === 'Event' ? week.games[0].eventName : `Week ${week.week}`}</option>
                        ))}
                    </select>
                    <button onClick={handleCleanWeek} className="btn btn-warning font-bold py-2 px-4 rounded-lg disabled:opacity-60" disabled={!selectedSchedule || selectedWeekIndex === ''}>Clean Week Results</button>
                </div>
            </div>

            {/* Archive Season Section */}
            <div className="card p-6 rounded-lg shadow-md">
                <h3 className="heading-primary-1 text-2xl font-semibold mb-4">Archive a Season</h3>
                <p className="text-sm text-muted mb-4">Mark a season as historical. This adds an "archived" flag to its schedule.</p>
                <div className="flex flex-wrap items-center gap-4">
                    {loading ? <p>Loading seasons...</p> : (
                        <select
                            value={selectedArchiveSchedule}
                            onChange={(e) => setSelectedArchiveSchedule(e.target.value)}
                            className="flex-grow shadow border-low border rounded w-full md:w-auto py-2 px-3 text-muted leading-tight focus:outline-none focus:shadow-outline surface-low"
                            disabled={schedules.length === 0}
                        >
                            {schedules.map(schedule => (
                                <option key={schedule.id} value={schedule.id}>
                                    {schedule.name} {schedule.archived ? '(Archived)' : ''}
                                </option>
                            ))}
                        </select>
                    )}
                    <button onClick={handleArchiveSeason} className="btn btn-primary font-bold py-2 px-4 rounded-lg disabled:opacity-60" disabled={loading || schedules.length === 0}>Archive Season</button>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                    <button onClick={handleInitializeArchiveField} className="segmented-btn font-bold py-2 px-4 rounded-lg">Initialize Archive Field (Temporary)</button>
                </div>
            </div>

            {/* Season (Schedule) Deletion Section */}
            <div className="card p-6 rounded-lg shadow-md">
                <h3 className="heading-danger text-2xl font-semibold mb-4">Danger Zone: Delete a Season</h3>
                <div className="flex flex-wrap items-center gap-4">
                    {loading ? <p>Loading seasons...</p> : (
                        <select
                            value={selectedSchedule}
                            onChange={(e) => setSelectedSchedule(e.target.value)}
                            className="flex-grow shadow border-low border rounded w-full md:w-auto py-2 px-3 text-muted leading-tight focus:outline-none focus:shadow-outline surface-low"
                            disabled={schedules.length === 0}
                        >
                            {schedules.map(schedule => (
                                <option key={schedule.id} value={schedule.id}>
                                    {schedule.name}
                                </option>
                            ))}
                        </select>
                    )}
                    <button onClick={handleDeleteSchedule} className="btn btn-danger font-bold py-2 px-4 rounded-lg disabled:opacity-60" disabled={loading || schedules.length === 0}>Delete Season</button>
                </div>
            </div>

            <div className="mt-8 flex justify-center">
                <img src="https://placehold.co/500x250/a855f7/ffffff?text=Admin+Control+Panel" alt="Admin Settings" className="rounded-lg shadow-md max-w-full h-auto" />
            </div>
        </div>
    ); // This was the missing parenthesis
};